import type { RecordId, VersionId } from "@/domain/records/types";
import type { ContextSourceType } from "@/application/records/dtos/ContextTokensDTO";
import type {
  UploadAudioFileRequestDTO,
  UploadAudioResponseDTO,
  UploadTextFileRequestDTO,
  UploadTextResponseDTO,
  CheckSpaceResponseDTO,
} from "@/application/records/dtos/UploadDTO";
import type { RecordMetaResponse, DetailedReportResponse } from "@/lib/types";
import {
  normalizeRecordDetailError,
  RECORD_DETAIL_TRANSPORT_CONFIG,
  type RecordDetailRequestContext,
} from "@/lib/record-detail-transport";
import { handleUnauthorizedResponse } from "@/infrastructure/auth/authUtils";
import {
  buildHttpTransportError,
  isAbortError,
  isTransportError,
} from "@/infrastructure/http/transport-error";
import type { RecordsMetaBatchRequestOptions } from "@/lib/records-meta-batch";
import { createIdempotencyKey } from "../../http/request-id";
import { httpClient, type TransportRequestConfig } from "../../http/HttpClient";
import type { RecordBatchMetaResponse } from "@/lib/types";

type RecordBatchResponse = {
  records?: Record<string, RecordBatchMetaResponse>;
};

type ContextTokensApiResponse = {
  total_tokens: number;
  rate_per_100k: number;
};

/**
 * Adapter for Insight API integration
 * Clean Architecture - Infrastructure Layer
 */
export interface IInsightApiAdapter {
  getRecordMeta(
    recordId: RecordId,
    include?: string[]
  ): Promise<RecordMetaResponse>;
  getRecordBatchMeta(
    recordIds: RecordId[],
    options?: RecordsMetaBatchRequestOptions
  ): Promise<Record<string, RecordBatchMetaResponse>>;
  getContextTokens(
    recordIds: RecordId[],
    contextType: ContextSourceType,
    projectId?: string
  ): Promise<ContextTokensApiResponse | null>;
  getDetailedRecord(
    recordId: RecordId,
    version?: VersionId,
    requestContext?: RecordDetailRequestContext
  ): Promise<DetailedReportResponse>;
  getPublicRecord(
    recordId: RecordId,
    version?: VersionId,
    requestContext?: RecordDetailRequestContext
  ): Promise<DetailedReportResponse>;
  patchTranscriptUtterance(
    recordId: RecordId,
    utteranceId: number,
    newText: string,
    version?: VersionId | null
  ): Promise<void>;
  patchSpeakerName(
    recordId: RecordId,
    speaker: string,
    speakerName: string,
    version?: VersionId | null
  ): Promise<void>;
  patchUtteranceSpeaker(
    recordId: RecordId,
    utteranceId: number,
    newSpeaker: string,
    version?: VersionId | null
  ): Promise<void>;
  patchSpeakerRespondent(
    recordId: RecordId,
    speaker: string,
    isRespondent: boolean,
    version?: VersionId | null
  ): Promise<void>;
  switchPublicity(recordId: RecordId, isPublic: boolean): Promise<void>;
  updateRecords(request: {
    change_names: Array<{ id: string; name: string }>;
    for_archive: string[];
    for_delete: string[];
    delete_link: string[];
    assign_link?: Array<{ record_id: string; project_id: string }>;
  }): Promise<void>;
  uploadAudioFile(
    request: UploadAudioFileRequestDTO
  ): Promise<UploadAudioResponseDTO>;
  uploadTextFile(
    request: UploadTextFileRequestDTO
  ): Promise<UploadTextResponseDTO>;
  checkSpace(fileSizeInMb: number): Promise<CheckSpaceResponseDTO>;
  postTranscriptRefine(
    recordId: RecordId,
    version?: VersionId | null
  ): Promise<{ transcript_version: number }>;
  postQualityRecheck(
    recordId: RecordId,
    version?: VersionId | null
  ): Promise<void>;
}

/**
 * Insight API Adapter implementation
 * Handles all communication with Insight API endpoints
 */
export class InsightApiAdapter implements IInsightApiAdapter {
  constructor(
    private readonly baseUrl: string,
    private readonly authHeadersProvider?: () => Promise<Record<string, string>>
  ) {}

  async getRecordMeta(
    recordId: RecordId,
    include?: string[]
  ): Promise<RecordMetaResponse> {
    const url = new URL(`${this.baseUrl}/insight/${recordId}/meta`);

    if (include && include.length > 0) {
      url.searchParams.set("include", include.join(","));
    }

    const response = await this.makeRequest<RecordMetaResponse | null>(
      url.toString()
    );

    if (!response) {
      throw new Error("Insight API returned empty record meta response");
    }

    return response;
  }

  async getRecordBatchMeta(
    recordIds: RecordId[],
    options?: RecordsMetaBatchRequestOptions
  ): Promise<Record<string, RecordBatchMetaResponse>> {
    const include = options?.include;
    const shouldUsePost =
      options?.mode === "summary" ||
      recordIds.length > 10 ||
      Boolean(options?.signal);

    if (!shouldUsePost) {
      return this.requestLegacyRecordBatchMeta(recordIds, include, options);
    }

    try {
      const response = await this.makeRequest<RecordBatchResponse | null>(
        `${this.baseUrl}/insight/meta-batch`,
        {
          method: "POST",
          body: JSON.stringify({
            ids: recordIds,
            include: include?.length ? include : undefined,
            mode: options?.mode ?? "full",
          }),
          signal: options?.signal,
        },
        options?.transportConfig
      );

      const records = response?.records ?? {};
      if (
        options?.mode === "summary" &&
        recordIds.length > 0 &&
        Object.keys(records).length === 0
      ) {
        return this.requestLegacyRecordBatchMeta(recordIds, include, options);
      }

      return records;
    } catch (error) {
      if (
        options?.mode !== "summary" ||
        isAbortError(error) ||
        (isTransportError(error) && error.code === "REQUEST_ABORTED")
      ) {
        throw error;
      }

      return this.requestLegacyRecordBatchMeta(recordIds, include, options);
    }
  }

  async getContextTokens(
    recordIds: RecordId[],
    contextType: ContextSourceType,
    projectId?: string
  ): Promise<ContextTokensApiResponse | null> {
    if (!recordIds || recordIds.length === 0) {
      return { total_tokens: 0, rate_per_100k: 0 };
    }

    const url = new URL(`${this.baseUrl}/insight/context-tokens-batch/`);
    url.searchParams.set("ids", recordIds.join(","));
    url.searchParams.set("context_type", contextType);
    if (projectId) {
      url.searchParams.set("project_id", projectId);
    }

    return this.makeRequest<ContextTokensApiResponse | null>(url.toString());
  }

  async getDetailedRecord(
    recordId: RecordId,
    version?: VersionId,
    requestContext?: RecordDetailRequestContext
  ): Promise<DetailedReportResponse> {
    const url = new URL(`${this.baseUrl}/insight/${recordId}/`);

    if (version) {
      url.searchParams.set("version", version.toString());
    }

    let response: DetailedReportResponse | null;

    try {
      response = await this.makeRequest<DetailedReportResponse | null>(
        url.toString(),
        {},
        RECORD_DETAIL_TRANSPORT_CONFIG
      );
    } catch (error) {
      throw normalizeRecordDetailError(error, {
        scope: "private",
        recordId,
        version: version ?? null,
        ...requestContext,
      });
    }

    if (!response) {
      throw new Error("Insight API returned empty detailed record response");
    }

    return response;
  }

  async getPublicRecord(
    recordId: RecordId,
    version?: VersionId,
    requestContext?: RecordDetailRequestContext
  ): Promise<DetailedReportResponse> {
    const url = new URL(`${this.baseUrl}/public/${recordId}/`);

    if (version) {
      url.searchParams.set("version", version.toString());
    }

    let response: DetailedReportResponse | null;

    try {
      response = await this.makeRequest<DetailedReportResponse | null>(
        url.toString(),
        {},
        RECORD_DETAIL_TRANSPORT_CONFIG
      );
    } catch (error) {
      throw normalizeRecordDetailError(error, {
        scope: "public",
        recordId,
        version: version ?? null,
        ...requestContext,
      });
    }

    if (!response) {
      throw new Error("Insight API returned empty public record response");
    }

    return response;
  }

  async patchTranscriptUtterance(
    recordId: RecordId,
    utteranceId: number,
    newText: string,
    version?: VersionId | null
  ): Promise<void> {
    const url = new URL(
      `${this.baseUrl}/insight/${recordId}/transcript_utterance`
    );

    if (version !== null && version !== undefined) {
      url.searchParams.set("version", version.toString());
    }

    await this.makeRequest<undefined>(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ utterance_id: utteranceId, text: newText }),
    }, {
      idempotencyKey: createIdempotencyKey("transcript-utterance", recordId, utteranceId),
    });
  }

  async patchSpeakerName(
    recordId: RecordId,
    speaker: string,
    speakerName: string,
    version?: VersionId | null
  ): Promise<void> {
    const url = new URL(
      `${this.baseUrl}/insight/${recordId}/speaker_name`
    );

    if (version !== null && version !== undefined) {
      url.searchParams.set("version", version.toString());
    }

    await this.makeRequest<undefined>(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ speaker, speaker_name: speakerName }),
    }, {
      idempotencyKey: createIdempotencyKey("speaker-name", recordId, speaker),
    });
  }

  async patchUtteranceSpeaker(
    recordId: RecordId,
    utteranceId: number,
    newSpeaker: string,
    version?: VersionId | null
  ): Promise<void> {
    const url = new URL(
      `${this.baseUrl}/insight/${recordId}/transcript_utterance_speaker`
    );

    if (version !== null && version !== undefined) {
      url.searchParams.set("version", version.toString());
    }

    await this.makeRequest<undefined>(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ utterance_id: utteranceId, new_speaker: newSpeaker }),
    }, {
      idempotencyKey: createIdempotencyKey("utterance-speaker", recordId, utteranceId),
    });
  }

  async patchSpeakerRespondent(
    recordId: RecordId,
    speaker: string,
    isRespondent: boolean,
    version?: VersionId | null
  ): Promise<void> {
    const url = new URL(
      `${this.baseUrl}/insight/${recordId}/speaker_respondent`
    );

    if (version !== null && version !== undefined) {
      url.searchParams.set("version", version.toString());
    }

    await this.makeRequest<undefined>(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ speaker, is_respondent: isRespondent }),
    }, {
      idempotencyKey: createIdempotencyKey("speaker-respondent", recordId, speaker),
    });
  }

  async switchPublicity(recordId: RecordId, isPublic: boolean): Promise<void> {
    const url = new URL(`${this.baseUrl}/insight/public/${recordId}/switch`);

    await this.makeRequest<undefined>(url.toString(), {
      method: "PATCH",
      body: JSON.stringify({ is_public: isPublic }),
    }, {
      idempotencyKey: createIdempotencyKey("record-publicity", recordId, isPublic ? "public" : "private"),
    });
  }

  async updateRecords(request: {
    change_names: Array<{ id: string; name: string }>;
    for_archive: string[];
    for_delete: string[];
    delete_link: string[];
    assign_link?: Array<{ record_id: string; project_id: string }>;
  }): Promise<void> {
    const url = new URL(`${this.baseUrl}/insight/update/`);

    await this.makeRequest<undefined>(url.toString(), {
      method: "PUT",
      body: JSON.stringify(request),
    }, {
      idempotencyKey: createIdempotencyKey(
        "insight-update-records",
        request.change_names.length,
        request.for_archive.length,
        request.for_delete.length,
        request.delete_link.length
      ),
    });
  }

  async uploadAudioFile(
    request: UploadAudioFileRequestDTO
  ): Promise<UploadAudioResponseDTO> {
    const url = new URL(`${this.baseUrl}/insight/upload/`);

    return this.makeRequest<UploadAudioResponseDTO>(url.toString(), {
      method: "POST",
      body: JSON.stringify(request),
    }, {
      idempotencyKey: createIdempotencyKey("upload-audio", request.record_name ?? "audio"),
    });
  }

  async uploadTextFile(
    request: UploadTextFileRequestDTO
  ): Promise<UploadTextResponseDTO> {
    const url = new URL(`${this.baseUrl}/insight/upload_text/`);

    return this.makeRequest<UploadTextResponseDTO>(url.toString(), {
      method: "POST",
      body: JSON.stringify(request),
    }, {
      idempotencyKey: createIdempotencyKey("upload-text", request.record_name ?? "text"),
    });
  }

  async checkSpace(fileSizeInMb: number): Promise<CheckSpaceResponseDTO> {
    const url = new URL(`${this.baseUrl}/insight/check_space/`);

    return this.makeRequest<CheckSpaceResponseDTO>(url.toString(), {
      method: "POST",
      body: JSON.stringify({ file_size_in_mb: fileSizeInMb }),
    }, {
      idempotencyKey: createIdempotencyKey("check-space", fileSizeInMb),
    });
  }

  async postTranscriptRefine(
    recordId: RecordId,
    version?: VersionId | null
  ): Promise<{ transcript_version: number }> {
    const url = new URL(
      `${this.baseUrl}/insight/${recordId}/transcript_refine`
    );

    if (version !== null && version !== undefined) {
      url.searchParams.set("version", version.toString());
    }

    return this.makeRequest<{ transcript_version: number }>(url.toString(), {
      method: "POST",
    }, {
      idempotencyKey: createIdempotencyKey("transcript-refine", recordId, version ?? "latest"),
    });
  }

  async postQualityRecheck(
    recordId: RecordId,
    version?: VersionId | null
  ): Promise<void> {
    const url = new URL(`${this.baseUrl}/insight/${recordId}/quality_recheck`);

    if (version !== null && version !== undefined) {
      url.searchParams.set("version", version.toString());
    }

    await this.makeRequest<void>(
      url.toString(),
      {
        method: "POST",
      },
      {
        timeoutMs: 120_000,
        maxAttempts: 1,
        idempotencyKey: createIdempotencyKey(
          "quality-recheck",
          recordId,
          version ?? "latest"
        ),
      }
    );
  }

  /**
   * Generic request method with error handling
   */
  private async makeRequest<TResponse = unknown>(
    url: string,
    options: RequestInit = {},
    transportConfig: TransportRequestConfig = {}
  ): Promise<TResponse> {
    try {
      // Use HttpClient which handles automatic token refresh
      const response = await httpClient.request(url, options, transportConfig);

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorizedResponse();
          return null as TResponse;
        }

        throw buildHttpTransportError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          response.headers.get("X-Request-ID") ?? transportConfig.requestId
        );
      }

      if (response.status === 204) {
        return undefined as TResponse;
      }

      const raw = await response.text();
      if (!raw) {
        return undefined as TResponse;
      }

      try {
        return JSON.parse(raw) as TResponse;
      } catch {
        return raw as unknown as TResponse;
      }
    } catch (error) {
      if (
        isTransportError(error) && error.code === "REQUEST_ABORTED"
      ) {
        throw error;
      }

      if (
        isAbortError(error) ||
        error === "superseded" ||
        error === "scope-aborted"
      ) {
        throw new DOMException("Aborted", "AbortError");
      }

      console.error("InsightApiAdapter request failed:", error);
      if (isTransportError(error)) {
        throw error;
      }
      throw error instanceof Error ? error : new Error("Unknown error");
    }
  }

  private async requestLegacyRecordBatchMeta(
    recordIds: RecordId[],
    include?: string[],
    options?: RecordsMetaBatchRequestOptions
  ): Promise<Record<string, RecordBatchMetaResponse>> {
    const response = await this.makeRequest<RecordBatchResponse | null>(
      (() => {
        const url = new URL(`${this.baseUrl}/insight/meta-batch`);
        url.searchParams.set("ids", recordIds.join(","));

        if (include && include.length > 0) {
          url.searchParams.set("include", include.join(","));
        }

        return url.toString();
      })(),
      { signal: options?.signal },
      options?.transportConfig
    );

    return response?.records ?? {};
  }

  // buildHeaders method removed - HttpClient handles headers automatically
}
